import { useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { toast } from "sonner";
import { Ellipsis, Table2, Trash2, ZoomIn } from "lucide-react";

import { DocumentQuicklook, useQuicklookPage } from "./DocumentQuicklook";
import { useDocumentCover } from "../hooks/useDocumentCover";
import { isMachineScopeMismatch, machineScopeSentence } from "./MachineScopeMarker";
import { SourceCard, type SourceCardLabels } from "./v2/SourceCard";
import { SourceGroup, SourceList, SourceSection, type SourceGroupLabels } from "./v2/SourceList";
import type { SourceType } from "./v2/lib/sources";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "./v2/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./v2/ui/dropdown-menu";
import { IconButton } from "./v2/ui/icon-button";
import { InfoList, InfoRow } from "./v2/ui/info-row";
import { TooltipProvider } from "./v2/ui/tooltip";
import { cn } from "../lib/utils";
import type { AssistantReference } from "../types/assistant";
import { ReferenceType } from "../types/assistant";
import {
    usePresignedDocumentUrls,
    type PresignedUrlEntry,
} from "../hooks/usePresignedDocumentUrls";
import { handleDocumentAnchorClick, openDocument } from "../lib/openDocument";

/**
 * The sources an answer was drawn from, by type, as v2 SourceList rows. This component
 * owns the data and the open paths (signed document links, table rows, past cases,
 * tickets, the contact's history); the v2 parts only render.
 *
 * Every cited document is a SourceGroup, one cited page or several: the document's
 * title, date and machine marker once, closed so the list shows every title at a glance.
 * The title opens the document; the chevron shows a row per cited page ("S. 12") with
 * the excerpt the answer used.
 */

type FallbackReason = "pre_generation_failed" | "url_missing_for_id";

/**
 * PROXY-02 telemetry: log whenever the document anchor flow falls back to
 * the click-time `documentUrl` mutation path. We can use this signal post-ship
 * to measure how often pre-generation actually saves users from popup-blocker
 * intervention.
 *
 * Sentry is initialized globally in Venus + Neptune at runtime; we read the
 * window-level handle defensively so the helper also degrades cleanly under
 * jsdom (tests) and any embedded webview without Sentry.
 */
function emitFallbackBreadcrumb(documentId: string, reason: FallbackReason): void {
    const sentry =
        typeof window !== "undefined"
            ? (window as { Sentry?: { addBreadcrumb?: (b: object) => void } }).Sentry
            : undefined;
    if (sentry?.addBreadcrumb) {
        sentry.addBreadcrumb({
            category: "document_ref",
            level: "info",
            message: "document_ref_fallback_triggered",
            data: { documentId, reason },
        });
    } else {
        // TODO(PROXY-02): replace with proper Sentry import when @sentry/react
        // becomes a peer dependency of frontend-common.
        // eslint-disable-next-line no-console
        console.warn("document_ref_fallback_triggered", { documentId, reason });
    }
}

export function buildPageFragment(pageNumber: number | null | undefined): string {
    // Page numbers are 1-BASED all the way from docparse: it returns docling's
    // prov[0].page_no (1-based, 0 meaning "unknown"), the chunker carries that into the
    // Qdrant chunk metadata, and both readers of that metadata --
    // retrieval/contract.py (sourceDocumentPageNumber) and build_ontology.py
    // (ontology_provenance.page) -- pass it through unchanged. The PDF viewer's #page=
    // is 1-based too, so the two conventions already agree and nothing needs adding.
    // This used to do `+ 1`, which opened every cited document one page late.
    // 0 means "unknown" rather than "first page", so it gets no fragment.
    return pageNumber ? `#page=${pageNumber}` : "";
}

const GET_DOCUMENT_URL = gql`
  mutation GetSharedDocumentUrl($documentId: UUID!) {
    documentUrl(documentId: $documentId) {
      id
      filename
      presignedUrl
    }
  }
`;

const DOCUMENT_TRANSLATIONS = gql`
  query DocumentTranslations($documentIds: [UUID!]!, $language: String!) {
    documentTranslations(documentIds: $documentIds, language: $language) {
      documentId
      translationId
      name
    }
  }
`;

interface DocumentTranslation {
    documentId: string;
    translationId: string;
    name: string;
}

/**
 * A cited document as its variant in the reader's language (#574), where it has one: the
 * same page of the same manual, translated. Translated manuals keep their layout page for
 * page, so the cited page stays right. The excerpt is the passage the model read, in the
 * cited language, so it does not come along, and neither does its highlight.
 */
function inReaderLanguage(
    reference: AssistantReference,
    variants: Map<string, DocumentTranslation>,
): AssistantReference {
    if (reference.type !== ReferenceType.Document || !reference.sourceDocumentId) {
        return reference;
    }
    const variant = variants.get(reference.sourceDocumentId);
    if (!variant) return reference;
    return {
        ...reference,
        sourceDocumentId: variant.translationId,
        title: variant.name,
        excerpt: null,
        quoteStart: null,
    };
}

const GET_TABLE_ROW = gql`
  query GetSharedTableRow($tableId: UUID!, $pkValues: JSON!) {
    dataClientTableRow(tableId: $tableId, pkValues: $pkValues)
    dataClientTableRowExternalLinks(tableId: $tableId, pkValues: $pkValues) {
      columnName
      url
      isPrimary
    }
  }
`;

export interface ReferenceCardCallbacks {
    /**
     * Open only what a link names: a document's injected URL (`presignedUrls`) or a
     * source's own `externalLink`. Never sign a document URL, fetch a row or open the
     * row preview. For the customer card, which can do neither and must not show a
     * table's columns.
     */
    linksOnly?: boolean;
    /**
     * Called when the user clicks a conversation/message reference.
     * Venus: opens a ConversationDialog inline.
     * Neptune: opens the Venus archive in a new tab.
     */
    onOpenConversation?: (conversationId: string, messageId: string) => void;
    /**
     * Called when the user clicks an email reference in Outlook.
     * Neptune only. Falls back to onOpenConversation if it throws.
     */
    onOpenEmail?: (mseMessageId: string) => Promise<void>;
    /** External case URL template, e.g. "https://crm.example.com/cases/{id}" */
    caseIdUrlTemplate?: string | null;
    /**
     * Called when the agent toggles a source's selection state. Passes the 0-based
     * reference index. Selected (checked) sources are included; deselected ones are muted.
     */
    onToggleSource?: (sourceId: string) => void;
    /** Called when the agent permanently removes a Kundenhistorie fact from memory. */
    onRemoveMemoryFact?: (sourceId: string) => void;
    /**
     * Called when the agent clicks a Kundenhistorie fact to open the contact's CRM
     * entry. Receives the contact context id (→ /contacts/:id). Venus navigates
     * in-app; Neptune opens the Venus contact page in a new tab.
     */
    onOpenContact?: (contactContextId: string) => void;
    /**
     * Neptune only. When set, a reference that points to an external ticket
     * (i.e. a case id is derivable from it) is opened by calling this with the
     * case id instead of following its URL. Used by tenants whose ticket system
     * has no openable URL (Kramer/Advasco), where a ticket is opened by POSTing
     * its id. When omitted (Venus, other tenants), references behave unchanged.
     */
    onOpenExternalTicket?: ((caseId: string) => void) | null;
    /**
     * Called every time the agent opens a source, by any path: the card, its
     * document link, the cited-page view, an external ticket. Telemetry only;
     * opening does not wait for it.
     */
    onSourceOpened?: (reference: AssistantReference) => void;
}

/**
 * The external ticket case id a reference points to, or null if it doesn't
 * point to one. Live "similar case" references carry it directly on
 * `sourceConversationExternalId`; legacy EXTERNAL_TICKET references encode it in
 * their title as "Ticket #<id>" (the shape the caseid backfill produced).
 */
export function externalTicketCaseId(reference: AssistantReference): string | null {
    if (reference.sourceConversationExternalId) {
        return reference.sourceConversationExternalId;
    }
    if (reference.type === ReferenceType.ExternalTicket) {
        const match = /#\s*(\S+)/.exec(reference.title ?? "");
        if (match) return match[1];
    }
    return null;
}


const SOURCE_TYPE: Record<string, SourceType> = {
    [ReferenceType.Document]: "document",
    [ReferenceType.Message]: "message",
    [ReferenceType.Sql]: "database",
    [ReferenceType.Memory]: "memory",
    [ReferenceType.ExternalTicket]: "ticket",
};

interface SourceLabels {
    card: SourceCardLabels;
    group: SourceGroupLabels;
}

function useSourceLabels(): SourceLabels {
    const { t } = useTranslation();
    return useMemo(() => {
        const date = (formatted: string) => t("messageReferences.contentDate", { date: formatted });
        return {
            card: {
                date,
                page: (page: number) => t("messageReferences.page", { page }),
                newTab: t("messageReferences.newTab"),
                excerpt: t("messageReferences.excerpt"),
            },
            group: {
                date,
                count: (count: number) => t("messageReferences.passages", { count }),
                passagesToggle: t("messageReferences.showPassages"),
                newTab: t("messageReferences.newTab"),
            },
        };
    }, [t]);
}

/** How a row shows that the agent deselected its source, or that the answer points at it. */
interface RowState {
    highlightedReferenceIds?: Set<string> | null;
    deselectedSourceIds?: Set<string>;
    onReferenceHover?: (referenceId: string | null) => void;
}

/** The row props every source shares: its id for scroll-to and the hover sync. */
function rowProps(reference: AssistantReference, state: RowState) {
    return {
        "data-reference-card-id": reference.id,
        highlighted: !!state.highlightedReferenceIds?.has(reference.id),
        className: cn(state.deselectedSourceIds?.has(reference.id) && "opacity-40"),
        onMouseEnter: () => state.onReferenceHover?.(reference.id),
        onMouseLeave: () => state.onReferenceHover?.(null),
    };
}

/** One cited document: every page the answer cites, under the document's title. */
function DocumentSource({
    pages,
    labels,
    state,
    urls,
    isErrorUrls,
    linksOnly,
    onSourceOpened,
}: {
    pages: AssistantReference[];
    labels: SourceLabels;
    state: RowState;
    urls: Map<string, PresignedUrlEntry>;
    isErrorUrls: boolean;
    linksOnly?: boolean;
    onSourceOpened?: (reference: AssistantReference) => void;
}) {
    const { t } = useTranslation();
    const first = pages[0];
    const cover = useDocumentCover(first.sourceDocumentId);
    // The title opens the document itself, at its start; a cited page opens at that page.
    const { href, onOpen } = useDocumentOpen({
        reference: first,
        page: null,
        entry: first.sourceDocumentId ? urls.get(first.sourceDocumentId) : undefined,
        isErrorUrls,
        linksOnly,
        onSourceOpened,
    });
    // On every page: a mixed group would mean the grouping key and the scope stamp disagree.
    const machineScope = pages.every(isMachineScopeMismatch)
        ? machineScopeSentence(t, {
              expectedLabel: first.scopeExpectedLabel,
              sourceLabel: first.scopeSourceLabel,
              factLabel: first.scopeFactLabel,
          })
        : null;
    return (
        <SourceGroup
            title={first.title ?? ""}
            count={pages.length}
            // The passage the answer points at must be on screen, so its group opens.
            revealed={pages.some((page) => !!state.highlightedReferenceIds?.has(page.id))}
            date={first.contentDate}
            machineScope={machineScope}
            cover={cover}
            href={href}
            onOpen={onOpen}
            labels={labels.group}
        >
            {pages.map((reference) => (
                <DocumentPage
                    key={reference.id}
                    reference={reference}
                    labels={labels}
                    state={state}
                    entry={reference.sourceDocumentId ? urls.get(reference.sourceDocumentId) : undefined}
                    isErrorUrls={isErrorUrls}
                    linksOnly={linksOnly}
                    onSourceOpened={onSourceOpened}
                />
            ))}
        </SourceGroup>
    );
}

/**
 * How a cited document opens, at `page` or (null) at its start: the signed or injected
 * link when there is one, else a link signed on click. The tab opens inside the gesture,
 * before the mutation: a `window.open` after an await is what popup blockers stop.
 */
function useDocumentOpen({
    reference,
    page,
    entry,
    isErrorUrls,
    linksOnly,
    onSourceOpened,
}: {
    reference: AssistantReference;
    page: number | null | undefined;
    entry: PresignedUrlEntry | undefined;
    isErrorUrls: boolean;
    linksOnly?: boolean;
    onSourceOpened?: (reference: AssistantReference) => void;
}) {
    const { t } = useTranslation();
    const [getDocumentUrl] = useMutation<{
        documentUrl: { id: string; filename: string; presignedUrl: string };
    }>(GET_DOCUMENT_URL, {
        onError: (error) => {
            toast.error(t("messageReferences.downloadError"), {
                description: error.message || t("messageReferences.downloadErrorDesc"),
            });
        },
    });
    const documentId = reference.sourceDocumentId;
    const pageFragment = buildPageFragment(page);
    const href = entry ? `${entry.url}${pageFragment}` : undefined;
    const reportOpen = () => onSourceOpened?.(reference);

    const openSigned = () => {
        if (!documentId) return;
        if (linksOnly) {
            // Only the injected link opens; nothing is signed for this reader.
            if (href) window.open(href, "_blank", "noreferrer");
            return;
        }
        emitFallbackBreadcrumb(documentId, isErrorUrls ? "pre_generation_failed" : "url_missing_for_id");
        const tab = window.open("", "_blank");
        void getDocumentUrl({
            variables: { documentId },
            onCompleted: (data) => {
                void openDocument({ url: data.documentUrl.presignedUrl, pageFragment, tab, documentId, t });
            },
        });
    };

    let onOpen: ((event: MouseEvent<HTMLElement>) => void) | undefined;
    if (href) {
        onOpen = (event) => {
            reportOpen();
            handleDocumentAnchorClick(event, { url: entry!.url, pageFragment, documentId, t });
        };
    } else if (documentId && !linksOnly) {
        onOpen = () => {
            reportOpen();
            openSigned();
        };
    }
    return { href, onOpen, openSigned, reportOpen };
}

/**
 * One cited page: opens the document at that page, and its magnifier shows just the
 * page. Its own component so each page's preview is a hook of its own.
 */
function DocumentPage({
    reference,
    labels,
    state,
    entry,
    isErrorUrls,
    linksOnly,
    onSourceOpened,
}: {
    reference: AssistantReference;
    labels: SourceLabels;
    state: RowState;
    entry: PresignedUrlEntry | undefined;
    isErrorUrls: boolean;
    linksOnly?: boolean;
    onSourceOpened?: (reference: AssistantReference) => void;
}) {
    const { t } = useTranslation();
    const [quicklookOpen, setQuicklookOpen] = useState(false);
    const documentId = reference.sourceDocumentId;
    const page = reference.sourceDocumentPageNumber;
    const { href, onOpen, openSigned, reportOpen } = useDocumentOpen({
        reference,
        page,
        entry,
        isErrorUrls,
        linksOnly,
        onSourceOpened,
    });
    // Fetched on mount: the magnifier only exists when a page preview does.
    const quicklookPreview = useQuicklookPage(documentId, page, reference.excerpt);

    return (
        <SourceCard
            {...rowProps(reference, state)}
            type="document"
            // 0 is an unknown page, not the first one. The group already names the document,
            // so a passage without a page has no title: its excerpt is the link.
            title={page ? labels.card.page(page) : ""}
            href={href}
            onOpen={onOpen}
            snippet={reference.excerpt?.trim() || null}
            labels={labels.card}
            actions={
                quicklookPreview && documentId && page ? (
                    <>
                        <IconButton
                            icon={ZoomIn}
                            size="sm"
                            label={t("messageReferences.viewPage", { page })}
                            onClick={() => {
                                reportOpen();
                                setQuicklookOpen(true);
                            }}
                        />
                        <DocumentQuicklook
                            documentId={documentId}
                            page={page}
                            preview={quicklookPreview}
                            snippet={reference.excerpt}
                            documentName={reference.title}
                            open={quicklookOpen}
                            onOpenChange={setQuicklookOpen}
                            onOpenDocument={() => {
                                setQuicklookOpen(false);
                                reportOpen();
                                openSigned();
                            }}
                        />
                    </>
                ) : undefined
            }
        />
    );
}

// A cell of a cited DB row. A marked column opens its value in the tenant's own system.
function RowCellValue({ value, href }: { value: unknown; href?: string }) {
    if (!href) return <>{String(value)}</>;
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="relative z-10 font-semibold underline-offset-2 hover:underline"
            onClick={(event) => event.stopPropagation()}
        >
            {String(value)}
        </a>
    );
}

/** Every source but a document: a past case, a table row, a ticket, a fact about the contact. */
function ReferenceSource({
    reference,
    labels,
    state,
    callbacks,
}: {
    reference: AssistantReference;
    labels: SourceLabels;
    state: RowState;
    callbacks: ReferenceCardCallbacks;
}) {
    const { t } = useTranslation();
    const {
        linksOnly,
        onOpenConversation,
        onOpenEmail,
        caseIdUrlTemplate,
        onRemoveMemoryFact,
        onOpenContact,
        onOpenExternalTicket,
        onSourceOpened,
    } = callbacks;
    const [isRowModalOpen, setIsRowModalOpen] = useState(false);

    // A cited past-case row reads as the case it is: its number, never its table.
    const pastCase = reference.pastCase ?? null;
    const title = pastCase
        ? t("messageReferences.pastCaseTitle", { number: pastCase.caseNumber })
        : (reference.title ?? "");
    const isSql = reference.type === ReferenceType.Sql;
    const pkValues = reference.sourceDatabaseRowPkValues
        ? (JSON.parse(reference.sourceDatabaseRowPkValues) as Record<string, string>)
        : null;

    const { data: rowData } = useQuery<{
        dataClientTableRow: Record<string, unknown> | null;
        dataClientTableRowExternalLinks: { columnName: string; url: string; isPrimary: boolean }[];
    }>(GET_TABLE_ROW, {
        variables: { tableId: reference.sourceDatabaseTableId!, pkValues: pkValues! },
        skip: !!linksOnly || !isSql || !reference.sourceDatabaseTableId || !pkValues,
    });
    const rowRecord = rowData?.dataClientTableRow ?? null;
    // A row whose table has a column marked with a tenant identifier pattern (case id,
    // machine number) opens in the tenant's own system instead of the row preview
    // (#775). The link stored on the citation wins; the read-time one covers citations
    // made before the column was marked.
    const rowLinks = isSql ? (rowData?.dataClientTableRowExternalLinks ?? []) : [];
    const externalLink = reference.externalLink ?? rowLinks.find((link) => link.isPrimary)?.url ?? null;
    // Every marked column links, not just the one the row opens.
    const linkByColumn = new Map(rowLinks.map((link) => [link.columnName, link.url]));

    const reportOpen = () => onSourceOpened?.(reference);
    const externalCaseId = reference.sourceConversationExternalId;
    // POST-mode open (Kramer/Advasco): the ticket system has no openable URL, so
    // opening a ticket means handing its case id to the caller.
    const postTicketCaseId = onOpenExternalTicket ? externalTicketCaseId(reference) : null;
    const externalConversationHref =
        reference.type === ReferenceType.Message && caseIdUrlTemplate && externalCaseId
            ? caseIdUrlTemplate.replace("{id}", externalCaseId)
            : null;
    // Kundenhistorie facts open the contact's CRM entry, not the conversation a fact
    // happened to be extracted from.
    const contactId =
        reference.type === ReferenceType.Memory && onOpenContact ? reference.sourceContactContextId : null;

    const openMessage = async () => {
        if (onOpenEmail && reference.sourceMessageEmailMseMessageId) {
            try {
                await onOpenEmail(reference.sourceMessageEmailMseMessageId);
                return;
            } catch {
                // falls back to the conversation
            }
        }
        if (onOpenConversation && reference.sourceConversationId && reference.sourceConversationMessageId) {
            onOpenConversation(reference.sourceConversationId, reference.sourceConversationMessageId);
        }
    };

    let href: string | undefined;
    let onOpen: (() => void) | undefined;
    if (postTicketCaseId) {
        onOpen = () => {
            reportOpen();
            onOpenExternalTicket!(postTicketCaseId);
        };
    } else if (externalConversationHref) {
        href = externalConversationHref;
        onOpen = reportOpen;
    } else if (isSql && externalLink) {
        href = externalLink;
        onOpen = reportOpen;
    } else if (isSql && rowRecord) {
        onOpen = () => {
            reportOpen();
            setIsRowModalOpen(true);
        };
    } else if (contactId) {
        onOpen = () => {
            reportOpen();
            onOpenContact!(contactId);
        };
    } else if (reference.sourceConversationMessageId && (onOpenEmail || onOpenConversation)) {
        onOpen = () => {
            reportOpen();
            void openMessage();
        };
    }

    // A past case's text is the row's own plain text. Other sources show their excerpt,
    // a past case from the mail archive its summary; a table row its columns instead.
    const caseText = pastCase?.text?.trim() || null;
    const description =
        (reference.type === ReferenceType.Message && reference.sourceMessageSummary
            ? reference.sourceMessageSummary
            : reference.excerpt
        )?.trim() || null;
    let snippet: ReactNode = null;
    if (pastCase) snippet = caseText;
    else if (description && !(isSql && rowRecord)) {
        snippet = <span dangerouslySetInnerHTML={{ __html: description }} />;
    }

    const identifier =
        pkValues && !pastCase
            ? Object.values(pkValues).join(", ")
            : caseIdUrlTemplate && externalCaseId
              ? `#${externalCaseId}`
              : null;
    const columns = rowRecord ? Object.entries(rowRecord).filter(([, value]) => value != null) : [];

    let actions: ReactNode;
    if (externalLink && rowRecord) {
        // The row opens the tenant's system, which would hide the row itself.
        actions = (
            <IconButton
                icon={Table2}
                size="sm"
                label={t("messageReferences.showRow")}
                onClick={() => setIsRowModalOpen(true)}
            />
        );
    } else if (reference.type === ReferenceType.Memory && onRemoveMemoryFact) {
        actions = (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <IconButton icon={Ellipsis} size="sm" label={t("messageReferences.moreActions", { name: title })} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        icon={Trash2}
                        variant="destructive"
                        onSelect={() => onRemoveMemoryFact(reference.id)}
                    >
                        {t("messageReferences.removeFromMemory")}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <>
            <SourceCard
                {...rowProps(reference, state)}
                type={SOURCE_TYPE[reference.type] ?? "document"}
                title={title}
                href={href}
                onOpen={onOpen}
                identifier={identifier}
                date={reference.contentDate ?? pastCase?.date ?? null}
                machineScope={machineScopeSentence(t, {
                    expectedLabel: reference.scopeExpectedLabel,
                    sourceLabel: reference.scopeSourceLabel,
                    factLabel: reference.scopeFactLabel,
                })}
                snippet={snippet}
                actions={actions}
                labels={labels.card}
            >
                {/* A table row shows its columns; its key is the identifier already. */}
                {rowRecord && !pastCase && (
                    <InfoList layout="inline" className="mt-1">
                        {columns
                            .filter(([key]) => !pkValues || !(key in pkValues))
                            .map(([key, value]) => (
                                <InfoRow
                                    key={key}
                                    label={key}
                                    value={<RowCellValue value={value} href={linkByColumn.get(key)} />}
                                />
                            ))}
                    </InfoList>
                )}
            </SourceCard>
            {rowRecord && (
                <Dialog open={isRowModalOpen} onOpenChange={setIsRowModalOpen}>
                    <DialogContent size="xl">
                        <DialogHeader>
                            <DialogTitle>{title}</DialogTitle>
                        </DialogHeader>
                        <DialogBody>
                            <InfoList layout="inline">
                                {columns.map(([key, value]) => (
                                    <InfoRow
                                        key={key}
                                        label={key}
                                        value={<RowCellValue value={value} href={linkByColumn.get(key)} />}
                                    />
                                ))}
                            </InfoList>
                        </DialogBody>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

/**
 * Past-case count above which "Ähnliche Servicefälle" switches to compact rows.
 *
 * An enumerative question ("in welchen Fällen trat X auf?") makes the source
 * list the answer rather than the evidence for it, and at that point full
 * rows work against the reader: the agent is scanning and comparing, not
 * studying one case. Below the threshold nothing changes — a handful of rows
 * is readable and carries more context per case.
 *
 * Count-based rather than agent-signalled on purpose: asking the model to
 * classify its own answer shape adds a failure mode for a decision the item
 * count already makes correctly.
 */
const COMPACT_CASE_LIST_THRESHOLD = 5;

const SECTION_TYPE_KEYS: { key: string; type: (typeof ReferenceType)[keyof typeof ReferenceType] }[] = [
    { key: "messageReferences.documents", type: ReferenceType.Document },
    { key: "messageReferences.dbResults", type: ReferenceType.Sql },
    { key: "messageReferences.similarCases", type: ReferenceType.Message },
    { key: "messageReferences.ticketReferences", type: ReferenceType.ExternalTicket },
    { key: "messageReferences.customerMemory", type: ReferenceType.Memory },
];


/** The section a reference is listed in: a cited past-case row is a service case. */
function sectionType(reference: AssistantReference): string {
    return reference.pastCase ? ReferenceType.Message : reference.type;
}

export interface MessageReferencesProps extends ReferenceCardCallbacks {
    references: AssistantReference[] | undefined;
    highlightedReferenceIds?: Set<string> | null;
    onReferenceHover?: (referenceId: string | null) => void;
    /** Ids of the sources the agent deselected: their rows stay, muted. */
    deselectedSourceIds?: Set<string>;
    /** @deprecated Use highlightedReferenceIds instead */
    selectedReferenceId?: string | null;
    /** @deprecated Document opening is handled internally */
    onReferenceClick?: (referenceId: string, reference: AssistantReference) => void;
    /**
     * Pre-fetched presigned document URLs, injected by a parent that hoists the
     * bulk fetch above a collapsed container so it still runs on load (see
     * CustomerRequestSourceReferences). When set, this component renders from the injected
     * map and skips its own usePresignedDocumentUrls fetch.
     */
    presignedUrls?: Map<string, PresignedUrlEntry>;
    /** Whether the injected fetch is still in flight (only meaningful with presignedUrls). */
    isLoadingUrls?: boolean;
    /** Error state of the injected fetch (only meaningful with presignedUrls). */
    isErrorUrls?: boolean;
}

/** Stable empty array so the injected-URLs path never triggers an own fetch. */
const EMPTY_DOCUMENT_IDS: string[] = [];

export function MessageReferences({
    references: cited,
    highlightedReferenceIds: highlightedProp,
    onReferenceHover,
    deselectedSourceIds,
    selectedReferenceId,
    onOpenConversation,
    onOpenEmail,
    caseIdUrlTemplate,
    onToggleSource,
    onRemoveMemoryFact,
    onOpenContact,
    onOpenExternalTicket,
    onSourceOpened,
    linksOnly,
    presignedUrls,
    isLoadingUrls,
    isErrorUrls,
}: MessageReferencesProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { t, i18n } = useTranslation();
    const labels = useSourceLabels();

    // The card's own schema swaps server side and answers no lookup (linksOnly).
    const citedDocumentIds = useMemo(
        () =>
            (cited ?? [])
                .filter((r) => r.type === ReferenceType.Document && r.sourceDocumentId)
                .map((r) => r.sourceDocumentId!),
        [cited],
    );
    const { data: variantData } = useQuery<{ documentTranslations: DocumentTranslation[] }>(
        DOCUMENT_TRANSLATIONS,
        {
            variables: { documentIds: citedDocumentIds, language: i18n.language },
            skip: citedDocumentIds.length === 0 || !!linksOnly,
        },
    );
    const references = useMemo(() => {
        const variants = new Map(
            (variantData?.documentTranslations ?? []).map((v) => [v.documentId, v]),
        );
        return cited?.map((reference) => inReaderLanguage(reference, variants));
    }, [cited, variantData]);

    // When a parent injects pre-fetched URLs (because this list renders inside a
    // collapsed container that would otherwise defer the on-mount fetch until the
    // user expands it), render from the injected map and skip our own fetch.
    const urlsInjected = presignedUrls !== undefined;

    // Collect all document IDs across references (D-01: eager fetch on mount). With
    // injected URLs only the ones the parent could not know: a cited document shown as
    // its variant in the reader's language.
    const documentIds = useMemo(() => {
        const ids = (references ?? [])
            .filter((r) => r.type === ReferenceType.Document && r.sourceDocumentId)
            .map((r) => r.sourceDocumentId!);
        if (linksOnly) return EMPTY_DOCUMENT_IDS;
        if (!presignedUrls) return ids;
        // The parent's fetch is still in flight and covers these ids.
        if (isLoadingUrls) return EMPTY_DOCUMENT_IDS;
        const missing = ids.filter((id) => !presignedUrls.has(id));
        return missing.length ? missing : EMPTY_DOCUMENT_IDS;
    }, [references, presignedUrls, isLoadingUrls, linksOnly]);

    const own = usePresignedDocumentUrls(documentIds, {
        observedElementRef: containerRef,
    });
    const urls = useMemo(
        () => (presignedUrls ? new Map([...own.urls, ...presignedUrls]) : own.urls),
        [presignedUrls, own.urls],
    );
    const isError = urlsInjected ? !!isErrorUrls : own.isError;

    if (!references?.length) return null;

    const state: RowState = {
        highlightedReferenceIds:
            highlightedProp ?? (selectedReferenceId ? new Set([selectedReferenceId]) : null),
        deselectedSourceIds,
        onReferenceHover,
    };
    const callbacks: ReferenceCardCallbacks = { onOpenConversation, onOpenEmail, caseIdUrlTemplate, onToggleSource, onRemoveMemoryFact, onOpenContact, onOpenExternalTicket, onSourceOpened, linksOnly };

    const sections = SECTION_TYPE_KEYS.map(({ key, type }) => {
        const items = references.filter((r) => sectionType(r) === type);
        // A document cited on several pages counts once.
        const count = type === ReferenceType.Document ? byDocument(items).size : items.length;
        return { label: t(key), type, items, count };
    }).filter(({ items }) => items.length > 0);

    return (
        // `not-prose` opts this subtree out of the Tailwind Typography styles it
        // inherits when the panel renders inside the answer bubble's `prose` container.
        // The tooltip provider is the panel's own: the apps mount none, and a v2 row's
        // title tooltip throws without one.
        <TooltipProvider>
            <div ref={containerRef} className="not-prose flex flex-col gap-4 pb-2">
                {sections.map((section) => (
                    <SourceSection key={section.type} title={section.label} count={section.count}>
                        <SourceList
                            density={
                                section.type === ReferenceType.Message &&
                                section.items.length >= COMPACT_CASE_LIST_THRESHOLD
                                    ? "compact"
                                    : "default"
                            }
                        >
                            {section.type === ReferenceType.Document
                                ? [...byDocument(section.items)].map(([key, pages]) => (
                                      <DocumentSource
                                          key={key}
                                          pages={pages}
                                          labels={labels}
                                          state={state}
                                          urls={urls}
                                          isErrorUrls={isError}
                                          linksOnly={linksOnly}
                                          onSourceOpened={onSourceOpened}
                                      />
                                  ))
                                : section.items.map((reference) => (
                                      <ReferenceSource
                                          key={reference.id}
                                          reference={reference}
                                          labels={labels}
                                          state={state}
                                          callbacks={callbacks}
                                      />
                                  ))}
                        </SourceList>
                    </SourceSection>
                ))}
            </div>
        </TooltipProvider>
    );
}

/** The cited pages of each document, in citation order; a citation without a document id
 *  is a document of its own. */
function byDocument(items: AssistantReference[]): Map<string, AssistantReference[]> {
    const documents = new Map<string, AssistantReference[]>();
    for (const reference of items) {
        const key = reference.sourceDocumentId ?? reference.id;
        documents.set(key, [...(documents.get(key) ?? []), reference]);
    }
    return documents;
}
